import { H5 } from 'UIComponents/elements/headings';
import { CUSTOM_FILTER_ADDED } from '../../../filterQueryFormat/UsageSubActionTypes';
import { POPOVER_CONTENTS } from './MoreQuickFiltersButton';
import { USED_FILTERS_BAR } from '../../../filterQueryFormat/UsageActionTypes';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLoadMoreFieldsHandler } from '../../../hooks/useLoadMoreFieldsHandler';
import EditorConditionFieldOptions from '../../editor/EditorConditionFieldOptions';
import EditorFixedHeaderContainer from '../../editor/EditorFixedHeaderContainer';
import FilterUserAction from '../../../filterQueryFormat/FilterUserAction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import Raven from 'raven-js';
import { Button } from 'foundations-components/transitional/components/Button';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import debounce from 'transmute/debounce';
import styled from 'foundations-theming/utils';
import { LeftIcon, EditIcon } from 'foundations-assets';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BLOCKLISTED_PROPERTIES = [];
const AddQuickFiltersPopoverContentContainer = styled.div.withConfig({
  displayName: "AddQuickFiltersPopoverContent__AddQuickFiltersPopoverContentContainer",
  componentId: "sc-9pud5y-0"
})(["display:flex;flex-direction:column;height:450px;width:400px;"]);
const AddQuickFiltersPopoverContent = ({
  allPropertyNames,
  customPropertyNames,
  hasOverflowingQuickFilters,
  onAddCustomPropertyName,
  onClosePopover,
  setCurrentPopoverContent
}) => {
  const {
    getTrackerInstance,
    props: {
      baseFilterFamily,
      onUserAction
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [searchString, setSearchString] = useState('');
  const inputRef = useRef(null);
  const loadMoreFields = useLoadMoreFieldsHandler();
  const canEditCustomQuickFilters = customPropertyNames.length > 0;
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  const onSearchChange = useMemo(() => debounce(400, evt => {
    const value = evt.target.value;
    if (value) {
      setIsLoadingFields(true);
    }
    loadMoreFields(baseFilterFamily, 0, value.trim()).then(() => {
      setIsLoadingFields(false);
    }).catch(error => {
      Raven.captureException(error);
    });
    setSearchString(value);
  }), [baseFilterFamily, loadMoreFields]);
  useEffect(() => {
    return () => onSearchChange.cancel();
  }, [onSearchChange]);
  const onSelectField = (__filterFamily, field) => {
    var _getTrackerInstance;
    onAddCustomPropertyName(field.name);
    onClosePopover();
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('addedProperty', {
      primaryObjectTypeId: baseFilterFamily,
      version: '2',
      property: field.name
    });
    onUserAction(FilterUserAction({
      action: USED_FILTERS_BAR,
      subAction: CUSTOM_FILTER_ADDED,
      filterFamily: baseFilterFamily,
      property: field.name
    }));
  };
  return /*#__PURE__*/_jsxs(AddQuickFiltersPopoverContentContainer, {
    children: [hasOverflowingQuickFilters && /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "400",
      paddingBlockEnd: "0",
      paddingInlineStart: "400",
      children: /*#__PURE__*/_jsx(Button, {
        size: "sm",
        use: "transparent",
        onClick: () => setCurrentPopoverContent(POPOVER_CONTENTS.MORE_QUICK_FILTERS),
        padding: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "100",
          children: [/*#__PURE__*/_jsx(LeftIcon, {
            size: "100",
            color: "icon-core-subtle"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.back"
          })]
        })
      })
    }), canEditCustomQuickFilters && !hasOverflowingQuickFilters && /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      padding: "400",
      alignItems: "center",
      borderBlockEndWidth: "100",
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-subtle",
      children: /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "MorePopover-editFilter",
        use: "link",
        onClick: () => setCurrentPopoverContent(POPOVER_CONTENTS.EDIT_QUICK_FILTERS),
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(EditIcon, {
            size: "200",
            color: "icon-core-subtle"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.editQuickFilters"
          })]
        })
      })
    }), /*#__PURE__*/_jsxs(EditorFixedHeaderContainer, {
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.addQuickFilter"
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        "aria-label": I18n.text('newCustomerDataFilters.fieldSelectFlow.fieldSearchInputAreaLabel'),
        children: /*#__PURE__*/_jsx(SearchInput, {
          "data-onboarding": "search-properties-input-field",
          "data-selenium-test": "fr-filter-search-input",
          inputRef: inputRef,
          onChange: onSearchChange
        })
      })]
    }), /*#__PURE__*/_jsx(EditorConditionFieldOptions, {
      filterFamily: baseFilterFamily,
      hiddenProperties: [...BLOCKLISTED_PROPERTIES, ...allPropertyNames],
      isLoadingFields: isLoadingFields,
      onSelectField: onSelectField,
      searchString: searchString
    })]
  });
};
export default AddQuickFiltersPopoverContent;